import React, { useEffect, useState } from "react";
import {
  LayoutDashboard,
  ChevronDown,
  LogOut,
  Building2,
  Bell,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "/api";

const CERTED_PRIMARY = "#3E0F8D";
const CERTED_SECONDARY = "#9564DD";
const CERTED_ACCENT = "#E4DA72";

const PartnerHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // =========================================================
  // STATE
  // =========================================================

  const [user, setUser] = useState(null);
  const [partner, setPartner] = useState(null);

  const [company, setCompany] = useState(null);
  const [companyLogo, setCompanyLogo] = useState("");

  const [showMenu, setShowMenu] = useState(false);

  // =========================================================
  // LOAD USER
  // =========================================================

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (!storedUser) {
        console.warn("No user found in localStorage");
        return;
      }

      const parsedUser = JSON.parse(storedUser);

      console.log("LOGGED USER:", parsedUser);

      setUser(parsedUser);
    } catch (error) {
      console.error("Failed to parse logged-in user:", error);
    }
  }, []);

  // =========================================================
  // LOAD PARTNER + COMPANY DETAILS
  // =========================================================

  useEffect(() => {
    let cancelled = false;

    const loadCompanyDetails = async () => {
      try {
        const storedUser = localStorage.getItem("user");

        if (!storedUser) {
          console.warn("No logged-in user found");
          return;
        }

        const loggedUser = JSON.parse(storedUser);

        console.log("USER FOR COMPANY LOAD:", loggedUser);

        // -----------------------------------------------------
        // 1. TRY PARTNER ID FROM LOCAL STORAGE
        // -----------------------------------------------------

        let partnerId =
          loggedUser?.partnerId ||
          loggedUser?.partner_id ||
          loggedUser?.partner?.id ||
          loggedUser?.partner?.partnerId ||
          null;

        // -----------------------------------------------------
        // 2. FIND PARTNER USING EMAIL
        // -----------------------------------------------------

        if (!partnerId && loggedUser?.email) {
          try {
            const partnerResponse = await fetch(
              `${API_BASE}/partners/all`
            );

            if (partnerResponse.ok) {
              const partnerData = await partnerResponse.json();

              console.log(
                "PARTNERS API RESPONSE:",
                partnerData
              );

              const partners = Array.isArray(partnerData)
                ? partnerData
                : partnerData?.data ||
                  partnerData?.content ||
                  partnerData?.partners ||
                  [];

              const loggedEmail = String(
                loggedUser.email
              )
                .trim()
                .toLowerCase();

              const currentPartner = partners.find((item) => {
                const partnerEmail = String(
                  item?.email ||
                    item?.partnerEmail ||
                    item?.user?.email ||
                    item?.userEmail ||
                    ""
                )
                  .trim()
                  .toLowerCase();

                return partnerEmail === loggedEmail;
              });

              if (currentPartner) {
                console.log(
                  "MATCHED PARTNER:",
                  currentPartner
                );

                partnerId =
                  currentPartner?.id ||
                  currentPartner?.partnerId;

                if (!cancelled) {
                  setPartner(currentPartner);
                }
              }
            }
          } catch (partnerError) {
            console.error(
              "Partner API error:",
              partnerError
            );
          }
        }

        // -----------------------------------------------------
        // 3. PARTNER ID NOT FOUND
        // -----------------------------------------------------

        if (!partnerId) {
          console.warn(
            "Partner ID not found.",
            loggedUser
          );

          return;
        }

        console.log(
          "FINAL PARTNER ID:",
          partnerId
        );

        // -----------------------------------------------------
        // 4. GET COMPANY DETAILS
        // -----------------------------------------------------

        const companyResponse = await fetch(
          `${API_BASE}/company-details/partner/${partnerId}`
        );

        if (!companyResponse.ok) {
          console.warn(
            "Company details API failed:",
            companyResponse.status
          );

          return;
        }

        const companyData =
          await companyResponse.json();

        console.log(
          "COMPANY API RESPONSE:",
          companyData
        );

        // -----------------------------------------------------
        // HANDLE DIFFERENT RESPONSE STRUCTURES
        // -----------------------------------------------------

        const companyDetails =
          companyData?.companyDetails ||
          companyData?.data ||
          companyData;

        if (cancelled) return;

        console.log(
          "FINAL COMPANY DETAILS:",
          companyDetails
        );

        setCompany(companyDetails);

        // -----------------------------------------------------
        // 5. COMPANY LOGO
        // -----------------------------------------------------

        const hasLogo =
          companyDetails?.logoFileName ||
          companyDetails?.logo ||
          companyDetails?.logoUrl;

        if (hasLogo) {
          const logoUrl =
            `${API_BASE}/company-details/partner/` +
            `${partnerId}/logo?t=${Date.now()}`;

          console.log(
            "COMPANY LOGO URL:",
            logoUrl
          );

          if (!cancelled) {
            setCompanyLogo(logoUrl);
          }
        } else {
          console.log(
            "No company logo found."
          );

          if (!cancelled) {
            setCompanyLogo("");
          }
        }
      } catch (error) {
        console.error(
          "Failed to load company details:",
          error
        );
      }
    };

    loadCompanyDetails();

    return () => {
      cancelled = true;
    };
  }, []);

  // =========================================================
  // DISPLAY NAME
  // =========================================================

  const displayName =
    user?.fullName ||
    user?.name ||
    user?.username ||
    user?.email ||
    "Partner";

  // =========================================================
  // USER ROLE
  // =========================================================

  const userRole =
    user?.role ||
    user?.userRole ||
    user?.user_role ||
    "PARTNER";

  // =========================================================
  // COMPANY NAME
  // =========================================================

  const companyName =
    company?.companyName ||
    company?.displayName ||
    company?.legalName ||
    company?.company_name ||
    company?.name ||
    partner?.companyName ||
    partner?.company ||
    partner?.company_name ||
    user?.companyName ||
    user?.company ||
    "Partner Portal";

  // =========================================================
  // COMPANY INITIAL
  // =========================================================

  const companyInitial =
    companyName
      ?.trim()
      ?.charAt(0)
      ?.toUpperCase() || "C";

  // =========================================================
  // USER INITIAL
  // =========================================================

  const userInitial =
    displayName
      ?.trim()
      ?.charAt(0)
      ?.toUpperCase() || "P";

  // =========================================================
  // NAVIGATION
  // =========================================================

  const goToDashboard = () => {
    setShowMenu(false);
    navigate("/partner-dashboard");
  };

  const goToProfile = () => {
    setShowMenu(false);
    navigate("/partner-profile");
  };

  const goToNotifications = () => {
    setShowMenu(false);
    navigate("/partner/partner-notifications");
  };

  const handleLogout = () => {
    setShowMenu(false);

    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("accessToken");

    navigate("/login");
  };

  // =========================================================
  // CLOSE DROPDOWN OUTSIDE
  // =========================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(
          ".certed-partner-profile-wrap"
        )
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <header className="certed-partner-header">

        {/* ================================================= */}
        {/* LEFT - COMPANY BRAND */}
        {/* ================================================= */}

        <div className="certed-partner-header-left">

          <div className="certed-company-brand">

            {/* COMPANY LOGO */}

            {/* <div className="certed-company-logo-box">

              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={companyName}
                  className="certed-company-logo-img"
                  onError={(event) => {
                    event.currentTarget.style.display =
                      "none";

                    const fallback =
                      event.currentTarget.parentElement?.querySelector(
                        ".certed-company-logo-fallback"
                      );

                    if (fallback) {
                      fallback.style.display = "flex";
                    }
                  }}
                />
              ) : null}

              <div
                className="certed-company-logo-fallback"
                style={{
                  display: companyLogo
                    ? "none"
                    : "flex",
                }}
              >
                {companyInitial}
              </div>

            </div> */}

            {/* COMPANY NAME */}

            {/* <div className="certed-company-brand-content">

              <div
                className="certed-company-title"
                title={companyName}
              >
                {companyName}
              </div>

              <div className="certed-company-subtitle">
                Partner Portal
              </div>

            </div> */}

          </div>

        </div>

        {/* ================================================= */}
        {/* RIGHT SIDE */}
        {/* ================================================= */}

        <div className="certed-partner-header-right">

          {/* DASHBOARD */}

          <button
            type="button"
            className={`certed-dashboard-button ${
              location.pathname ===
              "/partner-dashboard"
                ? "certed-dashboard-active"
                : ""
            }`}
            onClick={goToDashboard}
            title="Dashboard"
          >
            <LayoutDashboard size={18} />

            <span>
              Dashboard
            </span>
          </button>

          {/* NOTIFICATIONS */}

          <button
            type="button"
            className={`certed-notification-button ${
              location.pathname ===
              "/partner/partner-notifications"
                ? "certed-notification-active"
                : ""
            }`}
            onClick={goToNotifications}
            title="Notifications"
            aria-label="Notifications"
          >
            <Bell size={20} />

            <span className="certed-notification-badge">
              0
            </span>
          </button>

          {/* USER PROFILE */}

          <div className="certed-partner-profile-wrap">

            <button
              type="button"
              className="certed-profile-button"
              onClick={() =>
                setShowMenu((prev) => !prev)
              }
            >

              {/* AVATAR */}

              <div className="certed-user-avatar">
                {userInitial}
              </div>

              {/* USER INFORMATION */}

              <div className="certed-user-information">

                <span
                  className="certed-user-name"
                  title={displayName}
                >
                  {displayName}
                </span>

                <span className="certed-user-role">
                  {userRole}
                </span>

              </div>

              <ChevronDown
                size={17}
                className={`certed-dropdown-arrow ${
                  showMenu
                    ? "certed-arrow-open"
                    : ""
                }`}
              />

            </button>

            {/* ================================================= */}
            {/* DROPDOWN */}
            {/* ================================================= */}

            {showMenu && (
              <div className="certed-profile-dropdown">

                {/* DROPDOWN COMPANY HEADER */}

                <div className="certed-dropdown-company">

                  <div className="certed-dropdown-logo-box">

                    {companyLogo ? (
                      <img
                        src={companyLogo}
                        alt={companyName}
                        className="certed-dropdown-logo-img"
                        onError={(event) => {
                          event.currentTarget.style.display =
                            "none";

                          const fallback =
                            event.currentTarget.parentElement?.querySelector(
                              ".certed-dropdown-logo-fallback"
                            );

                          if (fallback) {
                            fallback.style.display =
                              "flex";
                          }
                        }}
                      />
                    ) : null}

                    <div
                      className="certed-dropdown-logo-fallback"
                      style={{
                        display: companyLogo
                          ? "none"
                          : "flex",
                      }}
                    >
                      {companyInitial}
                    </div>

                  </div>

                  <div className="certed-dropdown-company-info">

                    <strong
                      title={companyName}
                    >
                      {companyName}
                    </strong>

                    <span>
                      {userRole}
                    </span>

                  </div>

                </div>

                <div className="certed-dropdown-divider" />

                {/* COMPANY PROFILE */}

                <button
                  type="button"
                  className="certed-dropdown-item"
                  onClick={goToProfile}
                >
                  <Building2 size={17} />

                  <span>
                    Company Profile
                  </span>
                </button>

                {/* DASHBOARD */}

                <button
                  type="button"
                  className="certed-dropdown-item"
                  onClick={goToDashboard}
                >
                  <LayoutDashboard size={17} />

                  <span>
                    Dashboard
                  </span>
                </button>

                {/* NOTIFICATIONS */}

                <button
                  type="button"
                  className="certed-dropdown-item"
                  onClick={goToNotifications}
                >
                  <Bell size={17} />

                  <span>
                    Notifications
                  </span>
                </button>

                <div className="certed-dropdown-divider" />

                {/* LOGOUT */}

                <button
                  type="button"
                  className="certed-dropdown-item certed-logout-item"
                  onClick={handleLogout}
                >
                  <LogOut size={17} />

                  <span>
                    Logout
                  </span>
                </button>

              </div>
            )}

          </div>

        </div>
      </header>

      {/* ===================================================== */}
      {/* CSS - ALL NEW UNIQUE CLASS NAMES */}
      {/* ===================================================== */}

      <style>{`

        /* ================================================= */
        /* MAIN HEADER
        /* ================================================= */

        .certed-partner-header {
          width: 100%;
          height: 72px;

          background: #ffffff;

          border-bottom: 1px solid #e9e3f2;

          display: flex;
          align-items: center;
          justify-content: space-between;

          padding: 0 24px;

          box-sizing: border-box;

          position: sticky;
          top: 0;

          z-index: 1000;
        }

        /* ================================================= */
        /* LEFT SIDE
        /* ================================================= */

        .certed-partner-header-left {
          min-width: 0;

          display: flex;
          align-items: center;

          flex: 1;
        }

        /* ================================================= */
        /* COMPANY BRAND
        /* ================================================= */

        .certed-company-brand {
          display: flex;
          align-items: center;

          gap: 11px;

          min-width: 0;
          max-width: 520px;
        }

        /* ================================================= */
        /* COMPANY LOGO BOX
        /* ================================================= */

        .certed-company-logo-box {
          width: 48px;
          height: 48px;

          min-width: 48px;

          border-radius: 9px;

          background: #f7f3fc;

          border: 1px solid #e1d7ed;

          display: flex;
          align-items: center;
          justify-content: center;

          overflow: hidden;

          box-sizing: border-box;
        }

        .certed-company-logo-img {
          width: 100%;
          height: 100%;

          padding: 4px;

          object-fit: contain;

          display: block;

          box-sizing: border-box;
        }

        .certed-company-logo-fallback {
          width: 100%;
          height: 100%;

          align-items: center;
          justify-content: center;

          background: ${CERTED_PRIMARY};

          color: #ffffff;

          font-size: 21px;
          font-weight: 800;

          border-radius: 8px;
        }

        /* ================================================= */
        /* COMPANY TEXT
        /* ================================================= */

        .certed-company-brand-content {
          min-width: 0;

          display: flex;
          flex-direction: column;

          justify-content: center;

          line-height: 1.2;
        }

        .certed-company-title {
          max-width: 370px;

          color: ${CERTED_PRIMARY};

          font-size: 16px;
          font-weight: 800;

          white-space: nowrap;

          overflow: hidden;
          text-overflow: ellipsis;
        }

        .certed-company-subtitle {
          margin-top: 4px;

          color: #81798b;

          font-size: 11px;
          font-weight: 650;

          letter-spacing: 0.25px;
        }

        /* ================================================= */
        /* RIGHT SIDE
        /* ================================================= */

        .certed-partner-header-right {
          display: flex;
          align-items: center;

          gap: 10px;

          margin-left: 20px;
        }

        /* ================================================= */
        /* DASHBOARD BUTTON
        /* ================================================= */

        .certed-dashboard-button {
          height: 40px;

          padding: 0 14px;

          border: 1px solid #dfd5eb;

          border-radius: 9px;

          background: #ffffff;

          color: ${CERTED_PRIMARY};

          display: flex;
          align-items: center;
          justify-content: center;

          gap: 7px;

          font-size: 13px;
          font-weight: 700;

          cursor: pointer;

          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease,
            transform 0.2s ease;
        }

        .certed-dashboard-button:hover {
          background: #f7f2fc;

          border-color: #cdbbe2;

          transform: translateY(-1px);
        }

        .certed-dashboard-active {
          background: #f1eafa;

          border-color: #cdbbe2;
        }

        /* ================================================= */
        /* NOTIFICATION BUTTON
        /* ================================================= */

        .certed-notification-button {
          position: relative;

          width: 40px;
          height: 40px;

          padding: 0;

          border: 1px solid #dfd5eb;

          background: #ffffff;

          color: ${CERTED_PRIMARY};

          border-radius: 9px;

          display: flex;
          align-items: center;
          justify-content: center;

          cursor: pointer;

          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            transform 0.2s ease;
        }

        .certed-notification-button:hover {
          background: #f7f2fc;

          border-color: #cdbbe2;

          transform: translateY(-1px);
        }

        .certed-notification-active {
          background: #f1eafa;

          border-color: #cdbbe2;
        }

        /* ================================================= */
        /* NOTIFICATION BADGE
        /* ================================================= */

        .certed-notification-badge {
          position: absolute;

          top: -5px;
          right: -5px;

          min-width: 17px;
          height: 17px;

          padding: 0 4px;

          border-radius: 50%;

          background: ${CERTED_ACCENT};

          color: ${CERTED_PRIMARY};

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 9px;
          font-weight: 900;

          line-height: 1;

          border: 2px solid #ffffff;

          box-sizing: border-box;
        }

        /* ================================================= */
        /* PROFILE WRAPPER
        /* ================================================= */

        .certed-partner-profile-wrap {
          position: relative;
        }

        /* ================================================= */
        /* PROFILE BUTTON
        /* ================================================= */

        .certed-profile-button {
          border: 1px solid transparent;

          background: transparent;

          border-radius: 10px;

          padding: 5px 7px;

          display: flex;
          align-items: center;

          gap: 8px;

          cursor: pointer;

          transition:
            background 0.2s ease,
            border-color 0.2s ease;
        }

        .certed-profile-button:hover {
          background: #f8f5fc;

          border-color: #e2d8ed;
        }

        /* ================================================= */
        /* USER AVATAR
        /* ================================================= */

        .certed-user-avatar {
          width: 38px;
          height: 38px;

          min-width: 38px;

          border-radius: 50%;

          background: ${CERTED_PRIMARY};

          color: #ffffff;

          display: flex;
          align-items: center;
          justify-content: center;

          font-size: 15px;
          font-weight: 800;

          box-shadow:
            0 2px 7px rgba(62, 15, 141, 0.18);
        }

        /* ================================================= */
        /* USER INFORMATION
        /* ================================================= */

        .certed-user-information {
          min-width: 0;

          display: flex;
          flex-direction: column;

          align-items: flex-start;

          max-width: 160px;
        }

        .certed-user-name {
          max-width: 160px;

          color: #29232f;

          font-size: 13px;
          font-weight: 750;

          white-space: nowrap;

          overflow: hidden;
          text-overflow: ellipsis;
        }

        .certed-user-role {
          margin-top: 2px;

          color: #8a8193;

          font-size: 10px;
          font-weight: 700;

          text-transform: uppercase;
        }

        /* ================================================= */
        /* DROPDOWN ARROW
        /* ================================================= */

        .certed-dropdown-arrow {
          color: #777;

          transition: transform 0.2s ease;
        }

        .certed-arrow-open {
          transform: rotate(180deg);
        }

        /* ================================================= */
        /* DROPDOWN
        /* ================================================= */

        .certed-profile-dropdown {
          position: absolute;

          top: calc(100% + 9px);

          right: 0;

          width: 280px;

          background: #ffffff;

          border: 1px solid #e1d7eb;

          border-radius: 13px;

          box-shadow:
            0 14px 35px rgba(62, 15, 141, 0.15);

          padding: 8px;

          overflow: hidden;

          z-index: 2000;

          animation:
            certedDropdownOpen 0.16s ease-out;
        }

        @keyframes certedDropdownOpen {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* ================================================= */
        /* DROPDOWN COMPANY
        /* ================================================= */

        .certed-dropdown-company {
          display: flex;
          align-items: center;

          gap: 10px;

          padding: 10px;
        }

        .certed-dropdown-logo-box {
          width: 44px;
          height: 44px;

          min-width: 44px;

          border-radius: 8px;

          overflow: hidden;

          border: 1px solid #e1d7eb;

          background: #f7f3fc;

          display: flex;
          align-items: center;
          justify-content: center;
        }

        .certed-dropdown-logo-img {
          width: 100%;
          height: 100%;

          object-fit: contain;

          padding: 3px;

          box-sizing: border-box;
        }

        .certed-dropdown-logo-fallback {
          width: 100%;
          height: 100%;

          align-items: center;
          justify-content: center;

          background: ${CERTED_PRIMARY};

          color: #ffffff;

          font-size: 18px;
          font-weight: 800;
        }

        /* ================================================= */
        /* DROPDOWN COMPANY INFO
        /* ================================================= */

        .certed-dropdown-company-info {
          min-width: 0;

          display: flex;
          flex-direction: column;
        }

        .certed-dropdown-company-info strong {
          color: ${CERTED_PRIMARY};

          font-size: 13px;
          font-weight: 800;

          white-space: nowrap;

          overflow: hidden;
          text-overflow: ellipsis;
        }

        .certed-dropdown-company-info span {
          margin-top: 3px;

          color: #8a8193;

          font-size: 10px;
          font-weight: 700;

          text-transform: uppercase;
        }

        /* ================================================= */
        /* DIVIDER
        /* ================================================= */

        .certed-dropdown-divider {
          height: 1px;

          background: #eee8f3;

          margin: 5px 4px;
        }

        /* ================================================= */
        /* DROPDOWN ITEMS
        /* ================================================= */

        .certed-dropdown-item {
          width: 100%;

          border: none;

          background: transparent;

          color: #443a4d;

          border-radius: 8px;

          padding: 10px 11px;

          display: flex;
          align-items: center;

          gap: 10px;

          font-size: 13px;
          font-weight: 650;

          cursor: pointer;

          text-align: left;

          transition:
            background 0.2s ease,
            color 0.2s ease,
            padding-left 0.2s ease;
        }

        .certed-dropdown-item:hover {
          background: #f7f2fc;

          color: ${CERTED_PRIMARY};

          padding-left: 14px;
        }

        .certed-dropdown-item svg {
          color: ${CERTED_SECONDARY};

          flex-shrink: 0;
        }

        /* ================================================= */
        /* LOGOUT
        /* ================================================= */

        .certed-logout-item {
          color: #d43c3c;
        }

        .certed-logout-item svg {
          color: #d43c3c;
        }

        .certed-logout-item:hover {
          background: #fff1f1;

          color: #c62828;
        }

        /* ================================================= */
        /* TABLET
        /* ================================================= */

        @media (max-width: 900px) {

          .certed-partner-header {
            padding: 0 15px;
          }

          .certed-company-title {
            max-width: 230px;
          }

          .certed-dashboard-button span {
            display: none;
          }

          .certed-dashboard-button {
            width: 40px;

            padding: 0;
          }

        }

        /* ================================================= */
        /* MOBILE
        /* ================================================= */

        @media (max-width: 650px) {

          .certed-partner-header {
            height: 66px;

            padding: 0 10px;
          }

          .certed-company-brand {
            gap: 7px;
          }

          .certed-company-logo-box {
            width: 40px;
            height: 40px;

            min-width: 40px;
          }

          .certed-company-title {
            max-width: 145px;

            font-size: 13px;
          }

          .certed-company-subtitle {
            font-size: 9px;
          }

          .certed-partner-header-right {
            gap: 5px;

            margin-left: 8px;
          }

          .certed-notification-button {
            width: 38px;
            height: 38px;
          }

          .certed-dashboard-button {
            width: 38px;
            height: 38px;
          }

          .certed-profile-button {
            padding: 3px;
          }

          .certed-user-information {
            display: none;
          }

          .certed-profile-button
            .certed-dropdown-arrow {
            display: none;
          }

          .certed-profile-dropdown {
            right: -3px;

            width: 260px;
          }

        }

        /* ================================================= */
        /* SMALL MOBILE
        /* ================================================= */

        @media (max-width: 430px) {

          .certed-company-title {
            max-width: 110px;
          }

          .certed-company-logo-box {
            width: 36px;
            height: 36px;

            min-width: 36px;
          }

          .certed-company-subtitle {
            display: none;
          }

          .certed-notification-button {
            width: 36px;
            height: 36px;
          }

          .certed-dashboard-button {
            width: 36px;
            height: 36px;
          }

          .certed-user-avatar {
            width: 34px;
            height: 34px;

            min-width: 34px;
          }

          .certed-profile-dropdown {
            width: 250px;
          }

        }

      `}</style>
    </>
  );
};

export default PartnerHeader;